import { Component, computed, inject, input, output, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { InfoBannerComponent } from '../../../../shared/components/ui/info-banner/info-banner.component';
import { UserService } from '../../../user/service/user.service';
import { ADDON_CODE } from '../../../user/models/payment-plan.model';

const I18N_PREFIX = 'app.components.parentAiAssistant';

type PlanGateCopyKeys = {
  titleKey: string;
  messageKey: string;
  messageKeyWithLimit?: string;
};

function copyKeysForGate(code: string): PlanGateCopyKeys {
  switch (code) {
    case 'ai.db_assistant':
    case 'ai.kid_agent':
      return {
        titleKey: `${I18N_PREFIX}.planGateDbAssistantTitle`,
        messageKey: `${I18N_PREFIX}.planGateDbAssistant`,
      };
    case 'ai.general.requests':
      return {
        titleKey: `${I18N_PREFIX}.planGateGeneralRequestsTitle`,
        messageKey: `${I18N_PREFIX}.planGateGeneralRequests`,
        messageKeyWithLimit: `${I18N_PREFIX}.planGateGeneralRequestsWithLimit`,
      };
    case 'ai.kid.requests':
      return {
        titleKey: `${I18N_PREFIX}.planGateKidRequestsTitle`,
        messageKey: `${I18N_PREFIX}.planGateKidRequests`,
        messageKeyWithLimit: `${I18N_PREFIX}.planGateKidRequestsWithLimit`,
      };
    case 'ai.speech_to_text':
      return {
        titleKey: `${I18N_PREFIX}.planGateSpeechToTextTitle`,
        messageKey: `${I18N_PREFIX}.planGateSpeechToText`,
      };
    case 'ai.speech_to_text.minutes':
      return {
        titleKey: `${I18N_PREFIX}.planGateSpeechMinutesTitle`,
        messageKey: `${I18N_PREFIX}.planGateSpeechMinutes`,
        messageKeyWithLimit: `${I18N_PREFIX}.planGateSpeechMinutesWithLimit`,
      };
    default:
      return {
        titleKey: `${I18N_PREFIX}.planGateQuotaTitle`,
        messageKey: `${I18N_PREFIX}.planGateQuota`,
      };
  }
}

function isAiMeterGate(code: string): boolean {
  return code === 'ai.general.requests' || code === 'ai.kid.requests';
}

@Component({
  selector: 'app-parent-ai-plan-gate-banner',
  standalone: true,
  imports: [InfoBannerComponent, RouterLink, TranslatePipe],
  templateUrl: './parent-ai-plan-gate-banner.component.html',
  styleUrl: './parent-ai-plan-gate-banner.component.scss',
})
export class ParentAiPlanGateBannerComponent {
  readonly #translate = inject(TranslateService);
  readonly #userService = inject(UserService);

  planGateCode = input.required<string>();
  /** Optional quota limit from PAYMENTS_QUOTA_EXCEEDED meta. */
  planGateLimit = input<number | null>(null);
  dismissed = output<void>();

  upgradeLink = '/profile';
  upgradeQueryParams = { segment: 'subscriptions' };

  readonly boostLoading = signal(false);
  readonly boostError = signal(false);

  readonly #copyKeys = computed(() => copyKeysForGate(this.planGateCode()));

  readonly showAiBoost = computed(
    () =>
      isAiMeterGate(this.planGateCode()) && this.#userService.canPurchaseAddons(),
  );

  readonly boostLabel = computed(() => {
    const addon = this.#userService.findAddon(ADDON_CODE.AI_BOOST_10);
    const amount = addon?.grantAmount ?? 10;
    return this.#translate.instant(`${I18N_PREFIX}.planGateBuyAiBoost`, {
      count: amount,
    });
  });

  titleText = computed(() =>
    this.#translate.instant(this.#copyKeys().titleKey),
  );

  messageText = computed(() => {
    const limit = this.planGateLimit();
    const keys = this.#copyKeys();
    if (typeof limit === 'number' && limit > 0 && keys.messageKeyWithLimit) {
      return this.#translate.instant(keys.messageKeyWithLimit, { limit });
    }
    return this.#translate.instant(keys.messageKey);
  });

  dismiss(): void {
    this.dismissed.emit();
  }

  async buyAiBoost(): Promise<void> {
    if (this.boostLoading() || !this.showAiBoost()) return;
    this.boostLoading.set(true);
    this.boostError.set(false);
    try {
      const result = await this.#userService.startAddonCheckout.mutateAsync(
        ADDON_CODE.AI_BOOST_10,
      );
      if (result?.url) {
        window.location.href = result.url;
        return;
      }
      this.boostError.set(true);
    } catch {
      this.boostError.set(true);
    } finally {
      this.boostLoading.set(false);
    }
  }
}
